/** The control surface and the shortcut documentation at the same time (UX §3, §6.3).
 *
 * One full-width primary at 1.25 × the control height, then up to four secondaries at the
 * control height, each with its key printed on it. The secondary row is a `.grid-fit` in rem:
 * when fewer than four fit — a 360 px phone, or 1280 px at 200% — the overflow folds into
 * **More** from the right, so the two that matter most keep their place.
 *
 * `--fit-min` is 9.5 rem, not 7: a label has to survive the phone. At 7 rem three columns fit
 * a 390 px screen and "Open projector" truncates to "Op…", which is exactly the fault the
 * owner reported about "Screen" (2026-09-11). Two readable columns beat three unreadable ones.
 *
 * Nothing here uses `--control-h-sm`. This is a live surface.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../ui";
import { Keycap } from "./Keycap";

export type BarAction = {
  id: string;
  /** Already translated: the label carries a question stem often enough that the caller wins. */
  label: string;
  /** The stem is clipped, never the verb (§7.7). */
  stem?: string;
  keys?: string[];
  /** Said on hover while the control is live, where the label alone cannot carry it. */
  title?: string;
  onClick: () => void;
  disabled?: boolean;
  /** Printed as the refusal line when the control is pressed while disabled. */
  reason?: string;
  loading?: boolean;
};

type Props = {
  primary: BarAction | null;
  secondaries: BarAction[];
  /** Fired when a disabled control is pressed, so the reason is never silent. */
  onRefuse?: (reason: string) => void;
};

export function ActionBar({ primary, secondaries, onRefuse }: Props) {
  const { t } = useTranslation();
  const [more, setMore] = useState(false);
  // Four fit at 360 px; past that the overflow folds into More in right-to-left priority.
  const inline = secondaries.slice(0, 3);
  const overflow = secondaries.slice(3);

  const press = (a: BarAction) => () => {
    if (a.disabled) {
      if (a.reason) onRefuse?.(a.reason);
      return;
    }
    a.onClick();
  };

  return (
    <div className="live-bar flex flex-col gap-2 pt-3">
      {primary && (
        <Button
          block
          className="min-h-[calc(var(--control-h)*1.25)] text-lg"
          aria-keyshortcuts={primary.keys?.join(" ")}
          loading={primary.loading}
          disabled={primary.disabled}
          onClick={press(primary)}
          title={primary.disabled ? primary.reason && t(primary.reason) : primary.title}
        >
          <span className="shrink-0">{primary.label}</span>
          {primary.stem && <span className="min-w-0 flex-1 truncate text-left font-normal opacity-90">{primary.stem}</span>}
          {primary.keys && <span className="shrink-0"><Keycap keys={primary.keys} /></span>}
        </Button>
      )}
      <div className="grid-fit" style={{ "--fit-min": "9.5rem", gap: "0.5rem" } as React.CSSProperties}>
        {inline.map((a) => (
          <Button
            key={a.id}
            variant="secondary"
            aria-keyshortcuts={a.keys?.join(" ")}
            disabled={a.disabled}
            loading={a.loading}
            onClick={press(a)}
            title={a.disabled ? a.reason && t(a.reason) : a.title}
          >
            {/* Two short lines, never an ellipsis: at 360 px "Open projector" has to stay
                the words the teacher reads, not "Op…". */}
            <span className="line-clamp-2 min-w-0 text-center leading-tight">{a.label}</span>
            {a.keys && <Keycap keys={a.keys} />}
          </Button>
        ))}
        {overflow.length > 0 && (
          <Button variant="secondary" aria-expanded={more} onClick={() => setMore((v) => !v)}>
            <span className="line-clamp-2 min-w-0 text-center leading-tight">{t("live.bar.more")}</span>
          </Button>
        )}
      </div>
      {more && overflow.length > 0 && (
        <div className="grid-fit" style={{ "--fit-min": "9.5rem", gap: "0.5rem" } as React.CSSProperties}>
          {overflow.map((a) => (
            <Button
              key={a.id}
              variant="secondary"
              aria-keyshortcuts={a.keys?.join(" ")}
              disabled={a.disabled}
              onClick={press(a)}
              title={a.disabled ? a.reason && t(a.reason) : a.title}
            >
              <span className="line-clamp-2 min-w-0 text-center leading-tight">{a.label}</span>
              {a.keys && <Keycap keys={a.keys} />}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
